// ============================================================================
// API — Variantes (panel-cliente).
// ============================================================================

import { supabaseClient } from '@plataforma/supabase-client';
import { tieneFeature } from '@plataforma/utils';

import { obtenerEstadoSesion } from '../estado-sesion.js';

function empresaIdActual() {
  const sesion = obtenerEstadoSesion();
  return sesion.claims?.empresa_id ?? sesion.empresa?.id ?? null;
}

function empresaActual() {
  const sesion = obtenerEstadoSesion();
  return sesion.empresa ?? null;
}

function requiereVariantes(operacion) {
  const empresa = empresaActual();
  if (!tieneFeature(empresa, 'variantes')) {
    return Promise.resolve({
      data: null,
      error: {
        message: 'La funcionalidad Variantes no está disponible en tu plan. Mejora a Medio o Pro.',
        plan_requerido: 'Medio',
      },
    });
  }
  return operacion();
}

// ============================================================================
// Atributos
// ============================================================================

export async function listarAtributos() {
  const empresaId = empresaIdActual();
  return supabaseClient()
    .from('atributo')
    .select('id, nombre, tipo_visualizacion, creado_en')
    .eq('empresa_id', empresaId)
    .order('creado_en', { ascending: true });
}

// La tabla `atributo` no tiene columna `orden` (ver migración
// 20260101000003_dominio_catalogo.sql), por eso se omite aquí al persistir.
export async function crearAtributo(datos) {
  return requiereVariantes(() => {
    const empresaId = empresaIdActual();
    const datosAtributo = { ...datos };
    delete datosAtributo.orden;
    return supabaseClient()
      .from('atributo')
      .insert({ ...datosAtributo, empresa_id: empresaId })
      .select()
      .single();
  });
}

export async function actualizarAtributo(id, datos) {
  return requiereVariantes(() => {
    const datosAtributo = { ...datos };
    delete datosAtributo.orden;
    return supabaseClient().from('atributo').update(datosAtributo).eq('id', id).select().single();
  });
}

export async function eliminarAtributo(id) {
  return requiereVariantes(() => {
    return supabaseClient().from('atributo').delete().eq('id', id);
  });
}

// ============================================================================
// Valores de atributo
// ============================================================================

export async function listarValores(atributoId) {
  return supabaseClient()
    .from('atributo_valor')
    .select('id, atributo_id, valor, valor_color_hex, valor_imagen_url, orden')
    .eq('atributo_id', atributoId)
    .order('orden', { ascending: true });
}

export async function crearValor(atributoId, datos) {
  return requiereVariantes(() => {
    // atributo_valor.empresa_id es NOT NULL: debe viajar en el payload.
    const empresaId = empresaIdActual();
    const { orden, ...datosValor } = datos;
    return supabaseClient()
      .from('atributo_valor')
      .insert({ ...datosValor, atributo_id: atributoId, empresa_id: empresaId, orden: orden ?? 0 })
      .select()
      .single();
  });
}

export async function actualizarValor(id, datos) {
  return requiereVariantes(() => {
    return supabaseClient().from('atributo_valor').update(datos).eq('id', id).select().single();
  });
}

export async function eliminarValor(id) {
  return requiereVariantes(() => {
    return supabaseClient().from('atributo_valor').delete().eq('id', id);
  });
}

// ============================================================================
// Variantes de producto
// ============================================================================

export async function listarVariantesProducto(productoId) {
  return supabaseClient()
    .from('producto_variante')
    .select(
      'id, producto_id, sku, precio, stock, imagen_url, estado, creado_en, ' +
        'valores:producto_variante_valor(' +
        '  atributo_valor:atributo_valor_id(' +
        '    id, valor, valor_color_hex, valor_imagen_url, orden, ' +
        '    atributo:atributo_id(id, nombre, tipo_visualizacion)' +
        '  )' +
        ')',
    )
    .eq('producto_id', productoId)
    .order('creado_en', { ascending: true });
}

export async function crearVariante(productoId, datos) {
  return requiereVariantes(() => {
    // producto_variante.empresa_id es NOT NULL: debe viajar en el payload.
    const empresaId = empresaIdActual();
    return supabaseClient()
      .from('producto_variante')
      .insert({ ...datos, producto_id: productoId, empresa_id: empresaId })
      .select()
      .single();
  });
}

export async function actualizarVariante(id, datos) {
  return requiereVariantes(() => {
    return supabaseClient().from('producto_variante').update(datos).eq('id', id).select().single();
  });
}

export async function eliminarVariante(id) {
  return requiereVariantes(() => {
    return supabaseClient().from('producto_variante').delete().eq('id', id);
  });
}

// ============================================================================
// Relaciones variante-valor (para asignar atributos a una variante)
// ============================================================================

export async function asignarValoresVariante(varianteId, valores) {
  return requiereVariantes(() => {
    const filas = valores.map((valorId) => ({
      producto_variante_id: varianteId,
      atributo_valor_id: valorId,
    }));

    return supabaseClient().from('producto_variante_valor').insert(filas);
  });
}

export async function eliminarValoresVariante(varianteId) {
  return requiereVariantes(() => {
    return supabaseClient()
      .from('producto_variante_valor')
      .delete()
      .eq('producto_variante_id', varianteId);
  });
}

// ============================================================================
// Generación automática de combinaciones (producto cartesiano)
// ============================================================================

/**
 * Sugiere un SKU legible a partir del slug del producto y los nombres de los
 * valores seleccionados. Ejemplo: "camiseta" + ["Rojo","M"] -> "CAM-ROJO-M".
 * Es editable por el usuario; aquí solo se propone.
 *
 * @param {string} productoSlug
 * @param {string[]} valoresNombres
 */
export function sugerirSku(productoSlug, valoresNombres) {
  const prefijo =
    (productoSlug || 'VAR')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 3) || 'VAR';
  const sufijo = valoresNombres
    .map((v) =>
      String(v)
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, ''),
    )
    .filter(Boolean)
    .join('-');
  return sufijo ? `${prefijo}-${sufijo}` : prefijo;
}

/**
 * Producto cartesiano de grupos de ids. Cada grupo es un array de
 * atributo_valor_id; devuelve todas las combinaciones posibles (arrays de ids).
 * @param {string[][]} grupos
 * @returns {string[][]}
 */
export function productoCartesiano(grupos) {
  return grupos
    .filter((g) => g && g.length > 0)
    .reduce((acum, grupo) => acum.flatMap((combo) => grupo.map((v) => [...combo, v])), [[]]);
}

/**
 * Genera las variantes de un producto a partir de los valores seleccionados.
 * No crea combinaciones duplicadas: consulta las variantes existentes y omite
 * las que ya tengan exactamente el mismo conjunto de atributo_valor.
 *
 * @param {string} productoId
 * @param {Array<{ valores: Array<{ id: string, valor: string, atributo?: string }> }>} seleccion
 *   Cada elemento es una característica con sus valores marcados.
 * @param {string} [productoSlug]
 * @returns {Promise<{ creadas: number, omitidas: number, error: object | null }>}
 */
export async function generarCombinacionesVariantes(productoId, seleccion, productoSlug) {
  const grupos = seleccion.map((s) => (s.valores || []).map((v) => v.id));
  const combos = productoCartesiano(grupos);

  if (combos.length === 0 || (combos.length === 1 && combos[0].length === 0)) {
    return { creadas: 0, omitidas: 0, error: { message: 'Selecciona al menos un valor.' } };
  }

  // Claves de combinaciones existentes para evitar duplicados.
  const { data: existentes } = await listarVariantesProducto(productoId);
  const clavesExistentes = new Set(
    (existentes ?? [])
      .map((v) =>
        (v.valores ?? [])
          .map((x) => x.atributo_valor?.id)
          .filter(Boolean)
          .sort()
          .join('|'),
      )
      .filter(Boolean),
  );

  const nombresPorValor = {};
  for (const s of seleccion) {
    for (const v of s.valores ?? []) nombresPorValor[v.id] = v.valor;
  }

  let creadas = 0;
  let omitidas = 0;

  for (const combo of combos) {
    const clave = [...combo].sort().join('|');
    if (clavesExistentes.has(clave)) {
      omitidas += 1;
      continue;
    }

    const sku = sugerirSku(
      productoSlug,
      combo.map((id) => nombresPorValor[id] ?? id),
    );
    const { data: variante, error } = await crearVariante(productoId, {
      sku,
      precio: null,
      stock: 0,
      imagen_url: null,
      estado: 'visible',
    });
    if (error) {
      return { creadas, omitidas, error };
    }
    const { error: errorValores } = await asignarValoresVariante(variante.id, combo);
    if (errorValores) {
      return { creadas, omitidas, error: errorValores };
    }
    creadas += 1;
  }

  return { creadas, omitidas, error: null };
}
